import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Cart panel behaviour on top of Horizon's cart-items-component (design/updates/cart-panels):
 * - open: rows cascade in; opened while another side panel is open — it replaces it in place (no slide);
 * - «Щойно додано» stripe under the added line, folds after 4 s;
 * - quantity: number, line price, counters and the total in the button change at once (the server confirms),
 *   «−» on 1 removes the line; a slow answer (> 300 ms) dims the row and the total;
 * - removal (09.10, simple): × (or «−» on 1) folds the row away in one move and sends the removal at once; removals
 *   go to the server one after another and the cart is redrawn once, when the last one is answered — so quick
 *   removals never bring rows back; «Повернути» is in the toast (adds the item back);
 * - checkout: the button fills with Steel and ignores a second click.
 * Survives Horizon re-rendering the cart: state is re-applied after each morph.
 */
if (!window.khCartInit) {
  window.khCartInit = true;

  const ADDED_MS = 4000;
  const UNDO_MS = 5000;
  const SLOW_MS = 300;
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = () => window.Shopify?.routes?.root || '/';
  const i18n = () => JSON.parse(document.getElementById('kh-i18n')?.textContent || '{}');
  const cartEl = () => /** @type {HTMLElement | null} */ (document.querySelector('#cart-drawer cart-items-component'));
  const dialogEl = () => document.querySelector('#cart-drawer .kh-cart-dialog');
  const sectionIds = () =>
    [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

  /** «₴1 595»: narrow no-break space between thousands, kopecks only when present (as snippet kh-price-uah). */
  const formatPrice = (cents) => {
    const value = Math.round(cents);
    const whole = Math.floor(Math.abs(value) / 100)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const kop = Math.abs(value) % 100;
    return `${value < 0 ? '−' : ''}₴${whole}${kop ? `,${String(kop).padStart(2, '0')}` : ''}`;
  };
  const itemsLabel = (count) => {
    const forms = i18n().itemsCount || {};
    const rule = new Intl.PluralRules(document.documentElement.lang || 'uk').select(count);
    const template = forms[rule] && !String(forms[rule]).includes('missing') ? forms[rule] : forms.other || '{{ count }}';
    return String(template).replace('{{ count }}', String(count));
  };
  /** Sets text and lets the new value rise 4px. @param {Element | null} el @param {string} text */
  const tick = (el, text) => {
    if (!el || el.textContent === text) return;
    el.textContent = text;
    if (reduced()) return;
    el.classList.remove('kh-tick');
    void (/** @type {HTMLElement} */ (el).offsetWidth);
    el.classList.add('kh-tick');
  };

  /* ---------- Totals shown before the server answers ---------- */
  /** Count and total as shown now (server values or the optimistic ones). */
  const readShown = () => {
    const cart = cartEl();
    if (!cart) return null;
    const count = Number(cart.querySelector('[ref="cartItemCount"]')?.textContent) || 0;
    const totalEl = cart.querySelector('[data-kh-cart-total]');
    const total = Number(totalEl?.getAttribute('data-cents') || NaN);
    return { count, total: Number.isFinite(total) ? total : NaN };
  };
  /** Shows count / total (optimistic); total in cents. @param {number} count @param {number} total */
  const showTotals = (count, total) => {
    const cart = cartEl();
    tick(cart?.querySelector('[ref="cartItemCount"]') ?? null, String(count));
    tick(cart?.querySelector('[data-kh-cart-items]') ?? null, itemsLabel(count));
    const header = document.querySelector('[data-kh-cart-count]');
    if (header) {
      tick(header, String(count));
      /** @type {HTMLElement} */ (header).hidden = count === 0;
    }
    if (Number.isFinite(total)) {
      cart?.querySelectorAll('[data-kh-cart-total], [ref="cartTotal"]').forEach((el) => {
        el.setAttribute('data-cents', String(total));
        tick(el, formatPrice(total));
      });
      showFree(total);
    }
  };
  /** Free-delivery line and bar for an optimistic total (the server render sets them again). @param {number} total */
  const showFree = (total) => {
    const cart = cartEl();
    const box = /** @type {HTMLElement | null} */ (cart?.querySelector('[data-kh-free]') ?? null);
    if (!box) return;
    const from = Number(box.dataset.from) || 0;
    const ok = total >= from;
    box.classList.toggle('is-ok', ok);
    box.querySelector('.kh-cart__free-bar > span')?.setAttribute('style', `--kh-free: ${Math.min(100, Math.max(0, (total / from) * 100))}%`);
    const text = /** @type {HTMLElement | null} */ (box.querySelector('[data-kh-free-text]'));
    if (text) {
      if (ok) text.textContent = text.dataset.ok || '';
      else text.innerHTML = (text.dataset.left || '').replace('%amount%', `<span class="kh-price">${formatPrice(from - total)}</span>`);
    }
    cart?.querySelector('[data-kh-free-line]')?.classList.toggle('is-free', ok);
  };
  /** Line quantity × unit price, before the server answers. @param {HTMLElement} row @param {number} qty */
  const showLine = (row, qty) => {
    const unit = Number(row.dataset.unitPrice) || 0;
    const price = row.querySelector('[data-kh-line-price]');
    if (price) tick(price, formatPrice(unit * qty));
  };

  /** Server totals after each render: the total in the button carries its cents for the optimistic maths. */
  const syncFromServer = () => {
    const cart = cartEl();
    if (!cart) return;
    cart.querySelectorAll('.kh-cart__line[data-key]').forEach((row) => {
      const input = /** @type {HTMLInputElement | null} */ (row.querySelector('.kh-cart__qty'));
      if (!input || !(row instanceof HTMLElement)) return;
      // khServerQty: what the server last rendered (input default), khQty: what is shown
      if (row.dataset.khServerQty !== input.defaultValue) {
        row.dataset.khServerQty = input.defaultValue;
        row.dataset.khQty = input.value;
      }
    });
  };

  /* ---------- Opening: rows cascade in; replacing another side panel in place ---------- */
  document.addEventListener('theme-drawer:open', (event) => {
    const drawer = /** @type {HTMLElement} */ (event.target);
    if (drawer?.id !== 'cart-drawer') return;
    const dialog = dialogEl();
    if (!dialog) return;
    const other = /** @type {HTMLDialogElement | null} */ (document.querySelector('dialog.kh-side-panel[open]'));
    if (other) {
      // One panel at a time: the other one goes without sliding, the cart appears in place
      other.close();
      dialog.classList.add('is-swap');
      window.setTimeout(() => dialog.classList.remove('is-swap'), 400);
    } else if (!reduced()) {
      dialog.classList.add('is-intro');
      window.setTimeout(() => dialog.classList.remove('is-intro'), 900);
    }
    syncFromServer();
  });

  document.addEventListener('theme-drawer:close', (event) => {
    if (/** @type {HTMLElement} */ (event.target)?.id !== 'cart-drawer') return;
    justAdded.clear();
    markAdded();
  });

  /* ---------- «Щойно додано»: under the added line, folds after 4 s ---------- */
  /** @type {Set<string>} */
  const justAdded = new Set();
  /** @type {number | undefined} */
  let addedTimer;
  const markAdded = () => {
    document.querySelectorAll('.kh-cart__line[data-variant-id]').forEach((line) => {
      const added = justAdded.has(/** @type {HTMLElement} */ (line).dataset.variantId || '');
      const note = /** @type {HTMLElement | null} */ (line.querySelector('[data-kh-added]'));
      if (!note) return;
      if (added && note.hidden) {
        note.hidden = false;
        note.classList.remove('is-out');
        if (!reduced()) note.classList.add('is-in');
      } else if (!added && !note.hidden && !note.classList.contains('is-out')) {
        note.hidden = true;
      }
    });
  };
  const foldAdded = () => {
    document.querySelectorAll('.kh-cart__line [data-kh-added]:not([hidden])').forEach((note) => {
      note.classList.remove('is-in');
      note.classList.add('is-out');
      window.setTimeout(() => {
        /** @type {HTMLElement} */ (note).hidden = true;
        note.classList.remove('is-out');
      }, reduced() ? 0 : 220);
    });
    justAdded.clear();
  };

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const detail = /** @type {any} */ (event);
    if (detail.action !== 'add' || restoring) return;
    const ids = (detail.lines || [])
      .map((line) => String(line.merchandiseId || '').split('/').pop())
      .filter(Boolean);
    detail.promise
      ?.then(() => {
        justAdded.clear();
        ids.forEach((id) => justAdded.add(id));
        // Horizon morphs the cart right after the same promise resolves
        requestAnimationFrame(() => requestAnimationFrame(markAdded));
        window.setTimeout(markAdded, 400);
        window.clearTimeout(addedTimer);
        addedTimer = window.setTimeout(foldAdded, ADDED_MS);
      })
      .catch(() => {});
  });

  /* ---------- Server refused a quantity: Horizon resets the input, the optimistic numbers go back too ---------- */
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const detail = /** @type {any} */ (event);
    if (detail.action !== 'update') return;
    detail.promise?.catch(() => {
      window.setTimeout(() => {
        cartEl()
          ?.querySelectorAll('.kh-cart__line[data-key]')
          .forEach((row) => {
            if (!(row instanceof HTMLElement)) return;
            const server = Number(row.dataset.khServerQty);
            const current = Number(row.dataset.khQty);
            if (!Number.isFinite(server) || server === current) return;
            const unit = Number(row.dataset.unitPrice) || 0;
            row.dataset.khQty = String(server);
            showLine(row, server);
            const now = readShown();
            if (now) showTotals(now.count + (server - current), now.total + unit * (server - current));
            const plus = /** @type {HTMLButtonElement | null} */ (row.querySelector('[name="plus"]'));
            if (plus) plus.disabled = true; // the server said there is no more
          });
      }, 0);
    });
  });

  /* ---------- After every Horizon render: re-apply what the server markup does not know ---------- */
  const afterRender = () => {
    if (justAdded.size) markAdded();
    syncFromServer();
    // «−» on 1 removes the line (Horizon disables it at the minimum)
    document.querySelectorAll('.kh-cart__line [name="minus"]').forEach((button) => {
      const input = /** @type {HTMLInputElement | null} */ (button.closest('.kh-cart__line')?.querySelector('.kh-cart__qty'));
      if (input && Number(input.value) <= 1 && /** @type {HTMLButtonElement} */ (button).disabled) {
        /** @type {HTMLButtonElement} */ (button).disabled = false;
        button.setAttribute('data-kh-removes', '');
      } else if (input && Number(input.value) > 1) {
        button.removeAttribute('data-kh-removes');
      }
    });
    // A render while removals are on their way (another answer) must not bring their rows back
    hideRemoving();
    // The toast follows the cart's bottom block (summary → «Новинки» once the cart is empty)
    window.khPlaceCartToast?.();
  };
  // Also «disabled»: Horizon's quantity selector disables «−» at the minimum when it connects / updates — at 1 it
  // must stay active (it removes). afterRender only sets disabled = false, so this does not loop.
  new MutationObserver(afterRender).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['disabled'],
  });
  document.addEventListener('theme-drawer:open', afterRender);

  /* ---------- Slow answer: dim the touched row and the total after 300 ms ---------- */
  /** @type {HTMLElement | null} */
  let touchedRow = null;
  /** @type {number | undefined} */
  let slowTimer;
  // Watches only the class of cart-items-component, and touches classes only when they really change:
  // classList.remove() rewrites the attribute even for an absent class, which would re-trigger the observer.
  const watchBusy = () => {
    const cart = cartEl();
    if (!cart || cart.hasAttribute('data-kh-busy-watch')) return;
    cart.setAttribute('data-kh-busy-watch', '');
    new MutationObserver(() => {
      const busy = cart.classList.contains('cart-items-disabled');
      if (busy && slowTimer === undefined) {
        slowTimer = window.setTimeout(() => {
          if (touchedRow && !touchedRow.classList.contains('is-busy')) touchedRow.classList.add('is-busy');
          if (!cart.classList.contains('is-busy-total')) cart.classList.add('is-busy-total');
        }, SLOW_MS);
      } else if (!busy && slowTimer !== undefined) {
        window.clearTimeout(slowTimer);
        slowTimer = undefined;
        if (cart.classList.contains('is-busy-total')) cart.classList.remove('is-busy-total');
        cart.querySelectorAll('.kh-cart__line.is-busy').forEach((row) => row.classList.remove('is-busy'));
      }
    }).observe(cart, { attributes: true, attributeFilter: ['class'] });
  };
  watchBusy();
  document.addEventListener('theme-drawer:open', watchBusy);

  /* ---------- Clicks inside the cart ---------- */
  /** @type {boolean} */
  let replaying = false;
  // On window, capture: Horizon's declarative on:click runs as a document capture listener registered earlier,
  // and a window capture listener runs before it — so removal and the replay guard can stop Horizon's handler.
  window.addEventListener(
    'click',
    (event) => {
      if (replaying) return;
      const target = /** @type {HTMLElement} */ (event.target);
      const cart = target.closest?.('cart-items-component');
      if (!cart) return;

      const removeButton = target.closest('.kh-cart__remove, [name="minus"][data-kh-removes]');
      const stepButton = target.closest('.kh-stepper__btn');
      const checkout = target.closest('[data-kh-checkout]');

      // Horizon addresses quantity changes by line index: while removals are on their way, wait for the cart to settle,
      // then replay this click on the re-rendered button
      if (busy && (stepButton || checkout) && !removeButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const row = stepButton?.closest('.kh-cart__line');
        const key = row instanceof HTMLElement ? row.dataset.key : '';
        const name = stepButton?.getAttribute('name');
        settled().then(() => {
          const again = checkout
            ? document.querySelector('[data-kh-checkout]')
            : cartEl()?.querySelector(`.kh-cart__line[data-key="${CSS.escape(key || '')}"] [name="${name}"]`);
          if (again instanceof HTMLElement) {
            replaying = true;
            again.click();
            replaying = false;
          }
        });
        return;
      }

      // Any action hides an earlier error
      cart.querySelectorAll('.kh-cart__error:not(.hidden)').forEach((el) => el.classList.add('hidden'));

      if (removeButton) {
        const row = removeButton.closest('.kh-cart__line');
        if (!(row instanceof HTMLElement)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        removeLine(row);
        return;
      }

      if (stepButton instanceof HTMLButtonElement && !stepButton.disabled) {
        const row = stepButton.closest('.kh-cart__line');
        if (row instanceof HTMLElement) touchedRow = row;
        return;
      }

      if (checkout instanceof HTMLElement && !checkout.classList.contains('is-loading')) {
        checkout.classList.add('is-loading');
        // Walnut may open its own window over the page: give the button back after a while
        window.setTimeout(() => checkout.classList.remove('is-loading'), 8000);
      }
    },
    true
  );
  // Quantity: Horizon's selector (delegated on its component) has already changed the input when the click bubbles
  // up to the document — show the new line price, counters and total right away, the server confirms later
  document.addEventListener('click', (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest?.('.kh-cart .kh-stepper__btn');
    const row = button?.closest('.kh-cart__line');
    if (!(row instanceof HTMLElement)) return;
    const input = /** @type {HTMLInputElement | null} */ (row.querySelector('.kh-cart__qty'));
    // «before» = what the row shows (khQty, set after each render and each optimistic step): Horizon's own click
    // handler may run before ours, so the input already holds the new value
    const before = Number(row.dataset.khQty || input?.defaultValue || 0);
    const after = Number(input?.value || before);
    if (!input || after === before || after < 1) return;
    if (!reduced()) {
      input.classList.remove('kh-tick');
      void input.offsetWidth;
      input.classList.add('kh-tick');
    }
    row.dataset.khQty = String(after);
    showLine(row, after);
    const unit = Number(row.dataset.unitPrice) || 0;
    const now = readShown();
    if (now) showTotals(now.count + (after - before), now.total + unit * (after - before));
    // «+» stops at the online stock (input max); «−» on 1 removes the line
    const max = Number(input.max || Infinity);
    const plus = /** @type {HTMLButtonElement | null} */ (row.querySelector('[name="plus"]'));
    if (plus) plus.disabled = after >= max;
    const minus = /** @type {HTMLButtonElement | null} */ (row.querySelector('[name="minus"]'));
    if (minus && after <= 1) {
      minus.disabled = false;
      minus.setAttribute('data-kh-removes', '');
    }
  });

  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-kh-checkout].is-loading').forEach((el) => el.classList.remove('is-loading'));
  });

  /* ---------- Removal: one fold, one queue, one redraw ---------- */
  const FOLD_MS = 220;
  /** Keys folded away and not yet gone from the server's markup @type {Set<string>} */
  const removing = new Set();
  /** Cart requests on their way (removals and «Повернути») */
  let busy = 0;
  /** @type {Promise<unknown>} */
  let queue = Promise.resolve();
  /** The latest server answer with sections: drawn once the queue is empty */
  let latest = null;
  /** @type {(() => void)[]} */
  let waiting = [];
  /** @type {boolean} */
  let restoring = false;
  const settled = () => (busy ? new Promise((resolve) => waiting.push(() => resolve(null))) : Promise.resolve());

  /**
   * A row is out of the layout exactly while its key is being removed — whatever re-renders the cart (Horizon keeps
   * our class on a morphed row) — except while it is folding.
   */
  function hideRemoving() {
    cartEl()
      ?.querySelectorAll('.kh-cart__line[data-key]')
      .forEach((row) => {
        if (!(row instanceof HTMLElement) || row.dataset.khFolding) return;
        const gone = removing.has(row.dataset.key || '');
        if (row.classList.contains('is-gone') !== gone) row.classList.toggle('is-gone', gone);
      });
  }

  /** The row's height and opacity go to 0 together — one move — then it is out of the layout. @param {HTMLElement} row */
  function fold(row) {
    if (reduced()) {
      row.classList.add('is-gone');
      return;
    }
    row.dataset.khFolding = '1';
    row.style.height = `${row.offsetHeight}px`;
    row.style.overflow = 'hidden';
    void row.offsetHeight;
    row.style.transition = `height ${FOLD_MS}ms var(--kh-ease-drawer), opacity ${FOLD_MS}ms var(--kh-ease-out), padding ${FOLD_MS}ms var(--kh-ease-drawer)`;
    row.style.height = '0px';
    row.style.paddingTop = '0px';
    row.style.paddingBottom = '0px';
    row.style.opacity = '0';
    window.setTimeout(() => {
      delete row.dataset.khFolding;
      row.classList.toggle('is-gone', removing.has(row.dataset.key || ''));
      ['height', 'overflow', 'transition', 'padding-top', 'padding-bottom', 'opacity'].forEach((p) => row.style.removeProperty(p));
    }, FOLD_MS);
  }

  /** @param {string} path @param {object} body */
  const post = (path, body) =>
    fetch(`${root()}${path}`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: sectionIds().join(',') }),
    }).then((response) => (response.ok ? response.json() : Promise.reject(new Error(String(response.status)))));

  /** The cart as the server has it now (sections included) — after a failure or «Повернути». */
  const fresh = () => post('cart/update.js', { updates: {} });

  /** Queues a cart request; when the last one is answered, Horizon redraws the cart once from it. @param {() => Promise<any>} job */
  function enqueue(job) {
    busy += 1;
    queue = queue
      .then(job)
      .then((data) => {
        if (data?.sections) latest = data;
      })
      .catch(() => {})
      .finally(() => {
        busy -= 1;
        if (busy) return;
        const data = latest;
        latest = null;
        const done = () => {
          const resolved = waiting;
          waiting = [];
          resolved.forEach((resolve) => resolve());
        };
        // Removed lines are no longer in the server's markup; a line put back (same key) must show again
        removing.clear();
        if (!data) return done();
        const deferred = CartLinesUpdateEvent.createPromise();
        document.dispatchEvent(
          new CartLinesUpdateEvent({ action: 'remove', context: 'cart', lines: [], promise: deferred.promise })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
          detail: { items: data.items, itemCount: data.item_count, sections: data.sections, source: 'kh-cart-remove' },
        });
        // Horizon morphs on the same promise; give it a moment before replayed clicks
        window.setTimeout(done, 60);
      });
  }

  /** @param {HTMLElement} row */
  function removeLine(row) {
    const key = row.dataset.key || '';
    if (!key || removing.has(key)) return;
    const qty = Number(/** @type {HTMLInputElement | null} */ (row.querySelector('.kh-cart__qty'))?.value) || 1;
    const unit = Number(row.dataset.unitPrice) || 0;
    const variant = row.dataset.variantId || '';
    const title = row.querySelector('.kh-cart__name')?.textContent?.trim() || '';
    const canUndo = Boolean(variant) && !row.hasAttribute('data-kh-props');

    removing.add(key);
    fold(row);
    const now = readShown();
    if (now) showTotals(Math.max(0, now.count - qty), now.total - unit * qty);

    enqueue(() =>
      post('cart/change.js', { id: key, quantity: 0 }).catch(() => {
        // Still in the cart: the redraw brings the row back, and we say so
        removing.delete(key);
        window.khToast?.({ type: 'error', title: i18n().removeFailed || '' });
        return fresh();
      })
    );

    const texts = i18n();
    window.khToast?.({
      type: 'undo',
      title: String(texts.removed || '{{ title }}').replace('{{ title }}', title),
      action: canUndo ? texts.undo : '',
      onAction: canUndo
        ? () => {
            // Back at once in its place, growing in as Horizon shows added rows; the server follows
            removing.delete(key);
            const back = cartEl()?.querySelector(`.kh-cart__line[data-key="${CSS.escape(key)}"]`);
            if (back instanceof HTMLElement && !back.dataset.khFolding) {
              back.classList.remove('is-gone');
              if (!reduced()) {
                back.classList.add('adding');
                window.setTimeout(() => back.classList.remove('adding'), 300);
              }
            }
            const shown = readShown();
            if (shown) showTotals(shown.count + qty, shown.total + unit * qty);
            enqueue(() => {
              restoring = true;
              return post('cart/add.js', { items: [{ id: Number(variant), quantity: qty }] })
                .then(fresh)
                .finally(() => (restoring = false));
            });
          }
        : undefined,
    });
  }
}
